import React, { useEffect, useRef, useState, createContext } from "react";
import publicRouteList from "../../routes/publicRouteList";
import Navbar from "../frontend/Navbar";
import Footer from "../frontend/Footer";
import CartDrawer from "../../components/frontend/CartDrawer";
import WhatsAppButton from "../../components/frontend/WhatsAppButton";
import "../../assets/frontend/styles/frontsyle.css";
import { Redirect, Route, Switch, useLocation } from "react-router-dom";
import SEOHead from "../../components/common/SEOHead";
import axios from "axios";
import { customerToken } from "../../util/customerAuth";
import { loadActiveTheme } from "../../util/activeTheme";
import { themeButtonCssVars } from "../../themes/themeRegistry";
import {
  ActiveThemeContext,
  readActiveThemeCache,
  readThemeRecord,
  themeForThisVisit,
  themeHasSections,
  writeActiveThemeCache,
  writeThemeRecord,
} from "../../themes/themeCache";
import { loadDesignStyle } from "../../themes/designStyles";
import { preloadSections } from "../../themes/sections/components";
import MetaPixelTracker from "../../components/frontend/MetaPixelTracker";

const PRIVATE_PATH = /^\/(cart|checkout|account|login|register|wishlist|search|track-order|order-success|order-invoice|invoice|resetpassword)(\/|$)/;

function hideFromSearch(pathname, previewThemeKey) {
  return Boolean(previewThemeKey) || PRIVATE_PATH.test(pathname || "");
}

export const cartContext = createContext();
export const setContext = createContext();

export default function FrontendLayout() {
  const location = useLocation();
  const previewThemeKey = new URLSearchParams(location.search).get("preview_theme");
  const primedTheme = themeForThisVisit(window.__ECOM_ACTIVE_THEME__);
  const [Cart, setCart] = useState(0);
  const [activeTheme, setActiveTheme] = useState(primedTheme);
  const [themeStatus, setThemeStatus] = useState(themeHasSections(primedTheme) ? "ready" : "loading");
  const [themeVars, setThemeVars] = useState(() => themeButtonCssVars(primedTheme?.settings || {}));
  const [themeSettings, setThemeSettings] = useState(primedTheme?.settings || null);

  // Inside the theme editor's preview frame, the editor sends its unsaved
  // theme here so the page shows changes live. Only the editor on this same
  // site can do this; nothing is saved or cached.
  const framed = typeof window !== "undefined" && window.parent !== window;
  const liveTheme = useRef(null);
  const showTheme = (theme) => {
    setActiveTheme(theme);
    setThemeSettings(theme.settings);
    setThemeVars(themeButtonCssVars(theme.settings));
    setThemeStatus("ready");
  };
  useEffect(() => {
    if (!framed) return undefined;
    const onMessage = (event) => {
      if (event.origin !== window.location.origin || event.source !== window.parent) return;
      const data = event.data || {};
      if (data.type !== "ecom-theme-preview" || !data.theme || typeof data.theme.settings !== "object") return;
      liveTheme.current = { ...data.theme, is_preview: true };
      showTheme(liveTheme.current);
    };
    window.addEventListener("message", onMessage);
    window.parent.postMessage({ type: "ecom-preview-ready", path: location.pathname }, window.location.origin);
    return () => window.removeEventListener("message", onMessage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [framed, location.pathname]);

  // In the editor's frame, hovering a part of the page marked with
  // data-theme-group outlines it and shows an "Edit …" button that opens its
  // settings in the editor. Navigation is disabled while customizing so an
  // accidental link click cannot move the merchant away from the current page.
  useEffect(() => {
    if (!framed || !previewThemeKey) return undefined;
    const style = document.createElement("style");
    style.textContent = `
      [data-theme-group].is-theme-hover { outline: 2px dashed #2563eb; outline-offset: -2px; }
      .theme-edit-chip { position: fixed; z-index: 2147483000; padding: 6px 12px; border: 0; border-radius: 999px;
        background: #2563eb; color: #fff; font: 600 12px/1.2 Inter, system-ui, sans-serif; cursor: pointer;
        box-shadow: 0 6px 16px rgba(37, 99, 235, 0.35); }
      .theme-edit-chip[hidden] { display: none; }`;
    document.head.appendChild(style);
    const chip = document.createElement("button");
    chip.type = "button";
    chip.className = "theme-edit-chip";
    chip.hidden = true;
    document.body.appendChild(chip);
    let current = null;
    const place = () => {
      if (!current) return;
      const box = current.getBoundingClientRect();
      chip.style.top = `${Math.max(8, box.top + 8)}px`;
      chip.style.left = `${Math.max(8, Math.min(window.innerWidth - chip.offsetWidth - 8, box.right - chip.offsetWidth - 12))}px`;
    };
    const onOver = (event) => {
      if (event.target === chip) return;
      const region = event.target.closest ? event.target.closest("[data-theme-group]") : null;
      if (region === current) return;
      if (current) current.classList.remove("is-theme-hover");
      current = region;
      if (!region) {
        chip.hidden = true;
        return;
      }
      region.classList.add("is-theme-hover");
      chip.textContent = `Edit ${region.dataset.themeLabel || "this part"}`;
      chip.hidden = false;
      place();
    };
    const onChip = (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (current) window.parent.postMessage({ type: "ecom-edit-group", group: current.dataset.themeGroup }, window.location.origin);
    };
    const blockLinkNavigation = (event) => {
      const link = event.target?.closest?.("a[href]");
      if (!link) return;
      event.preventDefault();
      event.stopImmediatePropagation();
    };
    chip.addEventListener("click", onChip);
    document.addEventListener("click", blockLinkNavigation, true);
    document.addEventListener("auxclick", blockLinkNavigation, true);
    document.addEventListener("mouseover", onOver);
    window.addEventListener("scroll", place, true);
    return () => {
      document.removeEventListener("mouseover", onOver);
      document.removeEventListener("click", blockLinkNavigation, true);
      document.removeEventListener("auxclick", blockLinkNavigation, true);
      window.removeEventListener("scroll", place, true);
      if (current) current.classList.remove("is-theme-hover");
      chip.remove();
      style.remove();
    };
  }, [framed, previewThemeKey]);

  // As soon as the theme is known, fetch its design CSS, and on the homepage its
  // sections' code, in parallel with the page's data so nothing appears late.
  const onHomepage = location.pathname === "/";
  useEffect(() => {
    loadDesignStyle(activeTheme?.settings?.designStyle);
    if (onHomepage) preloadSections((activeTheme?.sections || []).map((section) => section.type));
  }, [activeTheme, onHomepage]);

  const cart = () => {
    if (customerToken()) {
      axios.get(`/api/cart`).then((res) => {
        setCart(res?.data?.cart?.length || 0);
      });
    }
  };

  useEffect(() => {
    let ismounted = true;
    if (ismounted) {
      cart();
    }
    return () => {
      ismounted = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    loadActiveTheme(previewThemeKey)
      .then((nextTheme) => {
        if (!mounted) return;
        if (liveTheme.current) {
          showTheme(liveTheme.current);
          return;
        }
        const settings = nextTheme?.settings;
        const savedTheme = previewThemeKey ? readThemeRecord(previewThemeKey) : readActiveThemeCache();
        if (!settings || !Object.keys(settings).length) {
          if (!mounted) return;
          if (savedTheme?.settings) {
            setActiveTheme(savedTheme);
            setThemeSettings(savedTheme.settings);
            setThemeVars(themeButtonCssVars(savedTheme.settings));
            setThemeStatus(themeHasSections(savedTheme) ? "ready" : "error");
          } else {
            setThemeStatus("error");
          }
          return;
        }
        const withSections = themeHasSections(nextTheme)
          ? nextTheme
          : themeHasSections(savedTheme)
            ? { ...nextTheme, sections: savedTheme.sections }
            : nextTheme;
        setActiveTheme(withSections);
        setThemeSettings(withSections.settings);
        setThemeVars(themeButtonCssVars(withSections.settings));
        setThemeStatus(themeHasSections(withSections) ? "ready" : "error");
        if (previewThemeKey) writeThemeRecord(withSections);
        else writeActiveThemeCache(withSections);
      })
      .catch(() => {
        if (!mounted) return;
        const savedTheme = previewThemeKey ? readThemeRecord(previewThemeKey) : readActiveThemeCache();
        if (savedTheme?.settings) {
          setActiveTheme(savedTheme);
          setThemeSettings(savedTheme.settings);
          setThemeVars(themeButtonCssVars(savedTheme.settings));
          setThemeStatus(themeHasSections(savedTheme) ? "ready" : "error");
          return;
        }
        setThemeStatus("error");
      });
    return () => {
      mounted = false;
    };
  }, [previewThemeKey]);

  if (themeStatus === "loading" && !themeSettings) {
    return (
      <ActiveThemeContext.Provider value={{ theme: null, status: "loading" }}>
        <div className="storefront-boot" role="status" aria-label="Loading store" />
      </ActiveThemeContext.Provider>
    );
  }

  return (
    <ActiveThemeContext.Provider value={{ theme: activeTheme, status: themeStatus }}>
    <div className="storefront-shell d-flex flex-column min-vh-100" data-design={themeSettings?.designStyle || undefined} style={themeVars}>
      {!previewThemeKey && <MetaPixelTracker />}
      <header data-theme-group="header" data-theme-label="Header">
        <cartContext.Provider value={Cart}>
          <Navbar setCartProps={setCart} themeSettings={themeSettings} />
        </cartContext.Provider>
      </header>

      {/* Global Slide-out Cart Drawer */}
      <CartDrawer themeSettings={themeSettings} />

      <main className="main-content">
        {hideFromSearch(location.pathname, previewThemeKey) && <SEOHead noindex />}
        <Switch>
          {publicRouteList.map(
            (route, index) =>
              route.component && (
                <Route
                  key={index}
                  path={route.path}
                  exact={route.exact}
                  name={route.name}
                  render={(props) => {
                    return (
                      <setContext.Provider value={setCart}>
                        <route.component {...props} />
                      </setContext.Provider>
                    );
                  }}
                />
              )
          )}
          <Redirect from="/admin" to="/admin/dashboard" />
        </Switch>
      </main>

      <div data-theme-group="footer" data-theme-label="Footer">
        <Footer themeSettings={themeSettings} />
      </div>
      <WhatsAppButton />
    </div>
    </ActiveThemeContext.Provider>
  );
}
